import { AmenityFormFields } from '@/components/amenities/AmenityFormFields';
import { type AmenityFormData } from '@/hooks/useAmenityForm';
import { Button, FormPageLayout } from '@startupquickstart/frontend';
import { ArrowLeft, Save } from 'lucide-react';
import { type Dispatch, type ReactNode, type SetStateAction } from 'react';

export interface AmenityFormLayoutProps {
  /** Heading shown in the top bar. */
  title: string;
  /** Optional sub-heading (e.g. the amenity name when editing). */
  subtitle?: string;
  /** Label for the primary submit button. */
  submitLabel: string;
  isSaving: boolean;
  lastSaved?: Date | null;
  /** Invoked by the back arrow, the Cancel buttons, and the browser-style exit. */
  onCancel: () => void;
  /** Invoked by the primary submit buttons. */
  onSubmit: () => void;
  /** Optional header-right extras (e.g. internal dev tools), placed before Cancel. */
  headerExtras?: ReactNode;
  formData: AmenityFormData;
  setFormData: Dispatch<SetStateAction<AmenityFormData>>;
}

/**
 * Shared single-page amenity form shell: a top bar (back + title + actions), the
 * centered {@link AmenityFormFields} content, and a footer with the same actions.
 * Used by both the global amenity form page and the hotel-scoped create page so
 * their layout stays identical.
 */
export function AmenityFormLayout({
  title,
  subtitle,
  submitLabel,
  isSaving,
  lastSaved,
  onCancel,
  onSubmit,
  headerExtras,
  formData,
  setFormData,
}: AmenityFormLayoutProps) {
  const actions = (
    <>
      <Button type="button" variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
      <Button type="button" onClick={onSubmit} loading={isSaving} icon={Save}>
        {submitLabel}
      </Button>
    </>
  );

  return (
    <FormPageLayout>
      <FormPageLayout.Header>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={onCancel}
            className="flex items-center justify-center h-9 w-9 rounded-lg border bg-card hover:bg-muted transition-colors cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-zinc-900">{title}</h1>
            {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {headerExtras}
          {actions}
        </div>
      </FormPageLayout.Header>

      <FormPageLayout.Content>
        <AmenityFormFields formData={formData} setFormData={setFormData} />
      </FormPageLayout.Content>

      <FormPageLayout.Footer lastSaved={lastSaved}>
        <div className="flex items-center gap-3">{actions}</div>
      </FormPageLayout.Footer>
    </FormPageLayout>
  );
}
